import { AddressItemType, Shop, ShopItem } from '@@types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from 'react-svg'
import { useMediaQuery } from 'usehooks-ts'

import i18n from '@app/i18n'

import { useAddressStore, useFetchMyAddresses, useLazyFetchAddressSuggestions, useMutateSaveAddress } from '@entities/Address'

import { useAuth } from '@features/auth'
import { useErrorInfoModal, useModalStore } from '@features/modal'
import { DeleteAddressModal } from '@features/shipping-methods/ui/DeleteAddressModal'

import useSlots from '@widgets/slots/hooks/useSlots'

import useShopTime from '@shared/hooks/useShopTime'
import { useYandexSuggest } from '@shared/hooks/useYandexSuggest'

import { PickupControls } from './PickupControls'
import PickupMap from './PickupMap'
import { ShopDetails } from './ShopDetails'
import { ShopList } from './ShopList'

import styles from './PickupView.module.scss'


enum ViewState {
  INITIAL = 'initial',
  DETAILS = 'details',
  MAP = 'map',
}

type Props = {
  setHeaderConfig: React.Dispatch<
    React.SetStateAction<{
      title: string;
      icon?: boolean;
      onClick?: () => void;
      isTabs?: boolean;
    }>
  >;
};

export default function Pickup ({ setHeaderConfig }: Props) {
  const { t } = useTranslation('modalDelivery')
  const { t: te } = useTranslation('errors')
  const { openErrorInfoModal } = useErrorInfoModal()
  const isTablet = useMediaQuery('(max-width: 1279px)')
  const [ view, setView ] = useState<ViewState>(ViewState.INITIAL)
  const [ isSearchMode, setSearchMode ] = useState(false)
  const [ shops, setShops ] = useState<Shop[] | null>(null)
  const [ selectedShop, setSelectedShop ] = useState<Shop | null>(null)
  const [ mapCenter, setMapCenter ] = useState<[number, number] | null>(null)
  const { openModal } = useModalStore()
  const [ saveAddress ] = useMutateSaveAddress()
  const { openSlotsModal } = useSlots()
  const { closeModal } = useModalStore()
  const { requireRegistration } = useAuth()
  
  // Селекторы для значений
  const selectedAddress = useAddressStore((state) => state.selectedAddress)
  
  // Функции можно брать из getState() — они стабильны
  const setSelectedAddress = useAddressStore((state) => state.setSelectedAddress)
  const setDeliveryType = useAddressStore((state) => state.setDeliveryType)
  const setShopId = useAddressStore((state) => state.setShopId)

  const { fetchSuggestions, data: suggestionData } = useLazyFetchAddressSuggestions()
  const { data: { myAddresses: myShops } = {}, refetch } = useFetchMyAddresses({
    showPickupShops: true,
  })
  const { open, close, getShopStatus } = useShopTime(
    selectedShop?.open_time || 0,
    selectedShop?.close_time || 0,
  )
  const {
    query: searchInputAddress,
    setQuery: setSearchInputAddress,
    suggestions,
  } = useYandexSuggest({
    apiKey: import.meta.env.VITE_YANDEX_MAPS_API_KEY_SEARCH,
    lang: i18n.language === 'kk' ? 'kk_KZ' : 'ru_RU',
  })

  useEffect(() => {
    const suggestion =
      suggestionData?.getAddressSuggestions?.suggestions?.[0]?.data

    if (suggestion?.latitude && suggestion?.longitude) {
      setMapCenter([ Number(suggestion.latitude), Number(suggestion.longitude) ])

      setSearchInputAddress('')
    }
  }, [ suggestionData, setSearchInputAddress ])

  const handleShopClick = (shop: Shop) => {
    setSelectedShop(shop)
    setView(ViewState.DETAILS)
  }

  const handleDeleteAddress = (shop: Shop) => {
    openModal(
      'deleteAddress',
      <DeleteAddressModal shop={shop as ShopItem} onSuccess={refetch} />,
      {
        title: t('shop.delete_title'),
      },
    )
  }

  const handleSelectShop = async (shop: Shop) => {
    const address = {
      shop_id: shop?.id,
      address: shop?.address || '',
      customerGeoPoint: {
        latitude: shop?.shopGeoPoint?.latitude,
        longitude: shop?.shopGeoPoint?.longitude,
      },
    }

    // Для авторизованных — проверяем есть ли уже такой магазин на сервере
    const isAlreadySavedOnServer = myShops?.some((addr) => addr?.shop_id === shop?.id)

    if (isAlreadySavedOnServer) {
      setSelectedAddress(address as AddressItemType)
      setShopId(shop?.id as string)
      setDeliveryType('pickup')
      closeModal('delivery')
      openSlotsModal()
      return
    }

    try {
      if (requireRegistration) {
        // Для анонима — просто выбираем адрес без сохранения в список
        setSelectedAddress(address as AddressItemType)
      }
      else {
        await saveAddress({
          variables: { addressInfo: address },
        })
      }

      setShopId(shop?.id as string)
      setDeliveryType('pickup')
      closeModal('delivery')
      openSlotsModal()
    }
    catch (err) {
      console.error('Error saving address:', err)
      openErrorInfoModal({
        description: te('select_shop'),
      })
    }
  }

  const handleControlsCallback = () => {
    setSearchMode(true)
    setView(ViewState.INITIAL)
  }

  useEffect(() => {
    if (isSearchMode || view === ViewState.DETAILS) {
      setHeaderConfig({
        title: t('touch-shop'),
        icon: true,
        onClick: () => {
          setView(ViewState.INITIAL)
          setSearchMode(false)
        },
        isTabs: false,
      })
    }
    else {
      setHeaderConfig({ title: t('address'), isTabs: true })
    }
    return () => {
      setHeaderConfig({ title: t('address'), isTabs: true })
    }
  }, [ isSearchMode, setHeaderConfig, t, view ])

  return (
    <div className={styles.root__container}>
      <div className={styles.root__content}>
        <PickupControls
          view={view}
          handleControlsCallback={handleControlsCallback}
          isSearchMode={isSearchMode}
          setSearchMode={setSearchMode}
          isTablet={isTablet}
          value={searchInputAddress}
          handleChange={setSearchInputAddress}
          placeholder={t('input.placeholder-pickup')}
        />
        {isTablet && isSearchMode && (
          <span
            onClick={() => setView(ViewState.MAP)}
            className={styles.root__touchMap}
          >
            <ReactSVG src="/icons/map-pin.svg" wrapper="span" />
            {t('button.touch-map')}
          </span>
        )}
        {(!isTablet || view === ViewState.MAP) && (
          <PickupMap
            center={mapCenter}
            onShopsDataUpdate={(data) => {
              if (data !== undefined) {
                setShops(data)
              }
            }}
            onShopClick={handleShopClick}
          />
        )}

        {view === ViewState.DETAILS ? (
          <ShopDetails
            handleSelectShop={handleSelectShop}
            selectedShop={selectedShop}
            open={open}
            close={close}
            getShopStatus={getShopStatus}
          />
        ) : !suggestions.length && !(isTablet && view === ViewState.MAP) ? (
          <ShopList
            handleDeleteAddress={handleDeleteAddress}
            shops={
              (isSearchMode
                ? shops
                : requireRegistration
                  ? (selectedAddress?.shop_id ? [ selectedAddress ] : [])
                  : myShops) as ShopItem[]
            }
            isSearchMode={!isSearchMode}
            handleShopClick={handleShopClick}
          />
        ) : (
          suggestions.map((el, index) => (
            <div
              onClick={() => {
                fetchSuggestions({
                  variables: { address: el.title.text },
                })
              }}
              className={styles.foundAddress}
              key={index}
            >
              <span className={styles.foundAddress__title}>
                {el.title.text}
              </span>
              <span className={styles.foundAddress__subtitle}>
                {el.subtitle?.text}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
